import { useMemo, useCallback } from 'react'
import { useOrderStore, ACTIVE_ORDER_STATES } from '@restaurant/core'
import type {
  OrderState,
  ActiveOrderTrackingStatus,
  OrderStatusToastIconName,
} from '@restaurant/core'

interface OrderStatusToastResult {
  visible: boolean
  title: string
  description: string
  iconName: OrderStatusToastIconName
  onToastClick: () => void
}

function getToastContent(state: OrderState, trackingStatus: ActiveOrderTrackingStatus | null, isDelivery: boolean): { title: string; description: string; iconName: OrderStatusToastIconName } {
  switch (state) {
    case 'newOrder':
    case 'submitting':
    case 'processing':
    case 'submitted':
    case 'confirming':
      return { title: 'Order Confirmed', description: 'Your order has been received', iconName: 'receipt' }
    case 'confirmed': {
      let preparingDescription = 'Your order is being prepared'
      if (trackingStatus?.estimatedCompletionTime) {
        const minutesRemaining = Math.ceil(
          (new Date(trackingStatus.estimatedCompletionTime).getTime() - Date.now()) / 60000
        )
        if (minutesRemaining > 0) {
          preparingDescription = `Ready in ~${minutesRemaining} minutes`
        }
      }
      return { title: 'Being Prepared', description: preparingDescription, iconName: 'inProgress' }
    }
    case 'enrouteToPickup':
    case 'arrivedAtPickup':
      return { title: 'Driver On The Way', description: 'Driver heading to restaurant', iconName: 'carAlt' }
    case 'pickedUp':
      if (isDelivery) {
        return {
          title: 'Out For Delivery',
          description: trackingStatus?.deliveryTimeRange
            ? `Estimated arrival ${formatTimeRange(trackingStatus.deliveryTimeRange)}`
            : 'Heading to you',
          iconName: 'carAlt',
        }
      }
      return { title: 'Order Ready', description: 'Your order is ready for pickup', iconName: 'checkBold' }
    case 'enrouteToDropoff':
      return {
        title: 'Out For Delivery',
        description: trackingStatus?.deliveryTimeRange
          ? `Estimated arrival ${formatTimeRange(trackingStatus.deliveryTimeRange)}`
          : 'Heading to you',
        iconName: 'carAlt',
      }
    case 'arrivedAtDropoff':
      return { title: 'Almost There', description: 'Your driver is nearby', iconName: 'carAlt' }
    default:
      return { title: 'Order In Progress', description: 'Tracking your order', iconName: 'inProgress' }
  }
}

function formatTimeRange(range: { startTime: string; endTime: string }): string {
  const start = new Date(range.startTime)
  const end = new Date(range.endTime)
  const format = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${format(start)}-${format(end)}`
}

export function useOrderStatusToast(): OrderStatusToastResult {
  const activeOrder = useOrderStore((s) => s.activeOrder)
  const trackingStatus = useOrderStore((s) => s.activeOrderTrackingStatus)
  const setTrackingSheetOpen = useOrderStore((s) => s.setTrackingSheetOpen)

  const isDelivery = activeOrder?.isDelivery ?? false

  const visible = useMemo(() => {
    if (!activeOrder) return false
    const state = trackingStatus?.currentState || activeOrder.status
    return ACTIVE_ORDER_STATES.includes(state)
  }, [activeOrder, trackingStatus?.currentState])

  const content = useMemo(() => {
    const state = trackingStatus?.currentState || activeOrder?.status || 'submitted'
    return getToastContent(state, trackingStatus, isDelivery)
  }, [activeOrder?.status, trackingStatus, isDelivery])

  const onToastClick = useCallback(() => {
    setTrackingSheetOpen(true)
  }, [setTrackingSheetOpen])

  return {
    visible,
    title: content.title,
    description: content.description,
    iconName: content.iconName,
    onToastClick,
  }
}
